import { Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/dialog';
import { Button } from '../../../components/ui/button';
import type { PublishSummary, PublishWarning } from '../publishSummary';

// Stage 7A D3 TASK 2 — pre-publish confirmation. Summarises what will be sent to
// Meta, surfaces the single-creative notes (Q1 warnings), and gates the final
// "Publish to Meta" action. Escape cancels (Radix); the Publish button autofocuses
// so Enter triggers it. While publishing, both actions disable + a spinner shows.

type Props = {
  open: boolean;
  summary: PublishSummary;
  warnings: PublishWarning[];
  publishing: boolean;
  onCancel: () => void;
  onConfirm: () => void;
};

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="grid grid-cols-[7rem_1fr] gap-2">
      <dt className="text-xs font-medium text-slate-500">{label}</dt>
      <dd className="text-sm text-slate-800">{value}</dd>
    </div>
  );
}

export function PublishConfirmationDialog({
  open,
  summary,
  warnings,
  publishing,
  onCancel,
  onConfirm,
}: Props) {
  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o && !publishing) onCancel();
      }}
    >
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Publish this ad?</DialogTitle>
          <DialogDescription>
            This creates a live campaign, ad set, and ad in Meta. Once published, the draft becomes
            read-only.
          </DialogDescription>
        </DialogHeader>

        <dl className="space-y-2 rounded-md border border-slate-200 bg-slate-50 p-3">
          <Row label="Campaign" value={summary.campaignName} />
          <Row label="Account" value={summary.accountName} />
          <Row label="Budget" value={summary.budget} />
          <Row label="Goal" value={summary.goalDestination} />
          <Row label="Schedule" value={summary.schedule} />
          <Row label="Audience" value={summary.audience} />
          <Row label="Creative" value={summary.creative} />
          <Row label="Placements" value={summary.placements} />
          {summary.verifiedAdvertiser && <Row label="Verified advertiser" value={summary.verifiedAdvertiser} />}
        </dl>

        {warnings.length > 0 && (
          <div className="rounded-md border border-amber-200 bg-amber-50 p-3">
            <p className="mb-1 text-xs font-semibold text-amber-800">Notes about this publish</p>
            <ul className="list-inside list-disc space-y-1 text-sm text-amber-800">
              {warnings.map((w) => (
                <li key={w.code}>{w.message}</li>
              ))}
            </ul>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onCancel} disabled={publishing}>
            Cancel
          </Button>
          <Button autoFocus onClick={onConfirm} disabled={publishing}>
            {publishing ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Publishing…
              </>
            ) : (
              'Publish to Meta'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
